import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, map, Observable} from 'rxjs';
import {
  ARRAY_VALUE_FIELD_TYPES,
  FieldBlock,
  FieldType,
  FormBlock,
  FormDefinition,
  ListBlock,
  OPTION_FIELD_TYPES
} from '../../shared/models/form-schema.model';
import {v4 as uuidv4} from 'uuid';
import {GlobalAlertService} from "../../../../_services/global-alert.service";
import {AlertLevel} from "../../../../enums/alert-level";

/** A field reference surfaced to the rule editor so authors pick keys instead of typing them. */
export interface FieldKeyRef {
  /** Dotted path usable by the rules engine (e.g. `address.city`). */
  path: string;
  label: string;
  type: FieldType;
  options?: { label: string; value: any }[];
  /** True when this field is a sibling within the same repeatable-list row as the edited block. */
  withinRow?: boolean;
}

const MAX_HISTORY = 50;

/**
 * **Two different questions, deliberately two predicates (#481).**
 *
 * Conflating them is how the builder ended up painting a red "this form is broken" badge on a perfectly working
 * customer form whose key happened to be `fullName`:
 *
 * 1. {@link isUnaddressableFieldKey} — *can the runtime resolve a control under this key at all?* This is a
 *    real defect, and it is the ONLY thing that may be reported as an error on an existing definition.
 * 2. {@link isSlugFieldKey} — *does this key match our house style?* An authoring rule for a key the author is
 *    creating or editing right now. A legacy key that fails it is not broken, only unfashionable.
 */

/**
 * The one character that genuinely breaks addressing: a **dot**.
 *
 * `AbstractControl.get(path: string)` splits its argument on `.` (Angular's `AbstractControl._find`), so for a
 * key like `Verzicht zus. Rentenversicherung` every *string* lookup answers `null` even though a control with
 * exactly that name exists. Since #470 the renderer uses the array overload, which does not split — but three
 * things still use the string form and still break:
 *
 * - a **rule** referencing the field (`RulesEngineService.getValueByPath` splits the path), so its
 *   `visibleWhen`/`requiredWhen` silently never fires;
 * - **server-error mapping** (`findControl` in `submission-errors.ts`), so a 422 for that field marks nothing;
 * - Angular itself logs `FormGroup keys cannot include '.'` in dev mode.
 *
 * Everything else was measured rather than assumed, and none of it breaks: uppercase, camelCase, a hyphen, an
 * umlaut, a leading/trailing underscore **and whitespace anywhere** all address correctly through every path
 * above. See `builder-state.service.spec.ts`, which pins that evidence.
 */
const UNADDRESSABLE_KEY_CHARACTER = /\./;

/**
 * Whether the runtime genuinely cannot address this key — the only condition that earns an error badge on an
 * existing definition. An absent/empty key is not reported here: it renders no key at all, so a "broken key"
 * marker beside nothing would be noise. Authoring refuses it via {@link isSlugFieldKey} instead.
 */
export function isUnaddressableFieldKey(key: string | null | undefined): boolean {
  return !!key && UNADDRESSABLE_KEY_CHARACTER.test(key);
}

/**
 * The house charset for a **newly authored** key: lowercase ASCII letters, digits and underscores, never
 * starting or ending with an underscore. Exactly what {@link BuilderStateService.slugForLabel} produces.
 *
 * This is style, not correctness — a key also travels as a JSON field key, an export column header and a rule
 * reference, and keeping every new one to one predictable shape avoids a family of quiet surprises. Enforce it
 * on what the author types; never use it to judge a key that is already in a stored definition.
 */
export const SLUG_FIELD_KEY_PATTERN = /^[a-z0-9](?:[a-z0-9_]*[a-z0-9])?$/;

/** Whether `key` matches the house slug charset — see {@link SLUG_FIELD_KEY_PATTERN}. */
export function isSlugFieldKey(key: string | null | undefined): boolean {
  return !!key && SLUG_FIELD_KEY_PATTERN.test(key);
}

@Injectable({
  providedIn: 'root'
})
export class BuilderStateService {
  constructor(private alertService: GlobalAlertService) {
    this.savedHash = this.hash(this.definition.value);
  }

  private definition = new BehaviorSubject<FormDefinition>(this.createEmptyDefinition());
  definition$ = this.definition.asObservable();

  private editingBlock = new BehaviorSubject<{ pageId: string, block: FormBlock } | null>(null);
  editingBlock$ = this.editingBlock.asObservable();

  // Bumped on every in-progress edit so the live preview re-renders without committing to the definition.
  private editingTick = new BehaviorSubject<number>(0);

  /**
   * Definition used by the live preview: the canonical definition with the block currently being edited
   * overlaid. This lets newly-configured logic/validators appear in the preview immediately, while the
   * canonical definition (and undo/dirty state) only changes on Save.
   */
  previewDefinition$: Observable<FormDefinition> =
    combineLatest([this.definition, this.editingBlock, this.editingTick]).pipe(
      map(([def, editing]) => editing ? this.overlayBlock(def, editing.block) : def)
    );

  /** Notify that the block being edited changed, so the live preview refreshes. */
  notifyEditingChanged() {
    this.editingTick.next(this.editingTick.value + 1);
  }

  private overlayBlock(def: FormDefinition, edited: FormBlock): FormDefinition {
    const clone: FormDefinition = this.clone(def);
    const replace = (blocks: FormBlock[]): boolean => {
      for (let i = 0; i < blocks.length; i++) {
        if (blocks[i].id === edited.id) {
          blocks[i] = this.clone(edited);
          return true;
        }
        const b = blocks[i];
        if (b.type === 'group' || b.type === 'list') {
          if (replace(b.blocks)) return true;
        }
      }
      return false;
    };
    for (const page of clone.pages) {
      if (replace(page.blocks)) break;
    }
    return clone;
  }

  // --- Undo / redo / dirty tracking -----------------------------------------
  private past: FormDefinition[] = [];
  private future: FormDefinition[] = [];
  private savedHash: string;

  private dirty = new BehaviorSubject<boolean>(false);
  dirty$ = this.dirty.asObservable();

  private canUndo = new BehaviorSubject<boolean>(false);
  canUndo$ = this.canUndo.asObservable();
  private canRedo = new BehaviorSubject<boolean>(false);
  canRedo$ = this.canRedo.asObservable();

  get currentDefinition(): FormDefinition {
    return this.definition.value;
  }

  get isDirty(): boolean {
    return this.dirty.value;
  }

  /**
   * Load a definition from an external source (e.g. server). Resets history and
   * marks the editor as pristine.
   */
  loadDefinition(def: FormDefinition) {
    this.past = [];
    this.future = [];
    this.definition.next(def);
    this.savedHash = this.hash(def);
    this.emitFlags();
  }

  /** Mark the current definition as the saved baseline (call after a successful save). */
  markSaved() {
    this.savedHash = this.hash(this.currentDefinition);
    this.emitFlags();
  }

  /**
   * Replace the whole definition (JSON editor, title/description edits). This
   * does NOT push to the undo stack to avoid flooding history on each keystroke.
   */
  setDefinition(def: FormDefinition) {
    this.definition.next(def);
    this.emitFlags();
  }

  /**
   * Re-emit the current definition and recompute dirty/flags. Used after
   * in-place edits to nested structures (e.g. cross-field rule groups) that
   * mutate the definition object directly.
   */
  touch() {
    this.definition.next(this.currentDefinition);
    this.emitFlags();
  }

  // --- Cross-field validations ----------------------------------------------
  addValidation() {
    this.mutate(def => {
      def.validations = def.validations ?? [];
      def.validations.push({
        id: uuidv4(),
        message: 'Neue Validierungsregel',
        when: {condition: 'AND', rules: []}
      });
    });
  }

  removeValidation(id: string) {
    this.mutate(def => {
      def.validations = (def.validations ?? []).filter(v => v.id !== id);
    });
  }

  // --- Undo / redo -----------------------------------------------------------
  undo() {
    const prev = this.past.pop();
    if (!prev) return;
    this.future.unshift(this.clone(this.currentDefinition));
    if (this.future.length > MAX_HISTORY) this.future.pop();
    this.definition.next(prev);
    this.emitFlags();
  }

  redo() {
    const next = this.future.shift();
    if (!next) return;
    this.past.push(this.clone(this.currentDefinition));
    if (this.past.length > MAX_HISTORY) this.past.shift();
    this.definition.next(next);
    this.emitFlags();
  }

  // --- Structural mutations --------------------------------------------------
  addPage() {
    this.mutate(def => {
      def.pages.push({
        id: uuidv4(),
        title: `Seite ${def.pages.length + 1}`,
        blocks: []
      });
    });
  }

  removePage(pageId: string) {
    if (this.currentDefinition.pages.length <= 1) {
      this.alertService.createAndShow("Fehler", "Es muss mindestens eine Seite vorhanden sein.", AlertLevel.error);
      return;
    }
    this.mutate(def => {
      def.pages = def.pages.filter(p => p.id !== pageId);
    });
  }

  addBlock(pageId: string, type: string, parentBlockId?: string) {
    this.mutate(def => {
      const page = def.pages.find(p => p.id === pageId);
      if (!page) return;
      const container = parentBlockId ? this.findBlockContainer(page.blocks, parentBlockId) : page.blocks;
      if (!container) return;
      container.push(this.createDefaultBlock(type, def));
    });
  }

  updateBlock(pageId: string, blockId: string, updates: Partial<FormBlock>) {
    this.mutate(def => {
      const page = def.pages.find(p => p.id === pageId);
      if (!page) return;
      const result = this.findBlockAndContainer(page.blocks, blockId);
      if (!result) return;
      const {container, index} = result;
      container[index] = {...container[index], ...updates} as FormBlock;
    });
  }

  removeBlock(pageId: string, blockId: string) {
    this.mutate(def => {
      const page = def.pages.find(p => p.id === pageId);
      if (!page) return;
      const result = this.findBlockAndContainer(page.blocks, blockId);
      if (!result) return;
      result.container.splice(result.index, 1);
    });
  }

  reorderBlocks(pageId: string, previousIndex: number, currentIndex: number, parentBlockId?: string) {
    this.mutate(def => {
      const page = def.pages.find(p => p.id === pageId);
      if (!page) return;
      const container = parentBlockId ? this.findBlockContainer(page.blocks, parentBlockId) : page.blocks;
      if (!container) return;
      const [removed] = container.splice(previousIndex, 1);
      container.splice(currentIndex, 0, removed);
    });
  }

  // --- Block editing ---------------------------------------------------------
  startEditingBlock(pageId: string, block: FormBlock) {
    this.editingBlock.next({pageId, block: this.clone(block)});
  }

  stopEditingBlock() {
    this.editingBlock.next(null);
  }

  saveEditedBlock(pageId: string, blockId: string, updatedBlock: FormBlock) {
    this.updateBlock(pageId, blockId, updatedBlock);
    this.stopEditingBlock();
  }

  // --- Rule-authoring support ------------------------------------------------
  /**
   * Fields the rule editor can reference for the block identified by
   * {@link forBlockId}, so authors pick keys from a dropdown instead of typing.
   *
   * When the edited block lives inside a repeatable list, the list row's
   * sibling fields are offered first as **within-row** references (bare keys,
   * evaluated against that row at runtime). Root-scope fields are always
   * offered too; list internals are otherwise excluded because their absolute
   * paths are index-dependent.
   */
  availableFieldKeys(forBlockId?: string): FieldKeyRef[] {
    const refs: FieldKeyRef[] = [];

    // 1. Within-row siblings, if the edited block is inside a repeatable list.
    const list = forBlockId ? this.findListAncestor(forBlockId) : null;
    if (list) {
      this.collectScopeRefs(list.blocks, '', forBlockId, true, refs);
    }

    // 2. Root-scope fields (group fields as dotted paths; list internals skipped).
    this.currentDefinition.pages.forEach(p => this.collectScopeRefs(p.blocks, '', forBlockId, false, refs));

    return refs;
  }

  private collectScopeRefs(
    blocks: FormBlock[],
    prefix: string,
    excludeId: string | undefined,
    withinRow: boolean,
    refs: FieldKeyRef[]
  ) {
    for (const block of blocks) {
      if (excludeId && block.id === excludeId) continue;
      if (block.type === 'group') {
        // Within a row, sibling keys are bare (no prefix) to match runtime scope resolution.
        const childPrefix = withinRow ? '' : prefix + block.key + '.';
        this.collectScopeRefs(block.blocks, childPrefix, excludeId, withinRow, refs);
      } else if (block.type === 'list') {
        // A nested list is its own (deeper) scope.
        continue;
      } else if (block.type !== 'static-text') {
        const field = block as FieldBlock;
        refs.push({
          path: prefix + field.key,
          label: field.label || field.key,
          type: field.type,
          options: field.options,
          withinRow
        });
      }
    }
  }

  /** The innermost repeatable list whose item template contains the given block, if any. */
  private findListAncestor(blockId: string): ListBlock | null {
    let found: ListBlock | null = null;
    const walk = (blocks: FormBlock[], list: ListBlock | null): boolean => {
      for (const b of blocks) {
        if (b.id === blockId) {
          found = list;
          return true;
        }
        if (b.type === 'group') {
          if (walk(b.blocks, list)) return true;
        } else if (b.type === 'list') {
          if (walk(b.blocks, b)) return true;
        }
      }
      return false;
    };
    this.currentDefinition.pages.forEach(p => walk(p.blocks, null));
    return found;
  }

  // --- Internal helpers ------------------------------------------------------
  private mutate(mutator: (def: FormDefinition) => void) {
    this.past.push(this.clone(this.currentDefinition));
    if (this.past.length > MAX_HISTORY) this.past.shift();
    this.future = [];
    const draft = this.clone(this.currentDefinition);
    mutator(draft);
    this.definition.next(draft);
    this.emitFlags();
  }

  private emitFlags() {
    this.dirty.next(this.hash(this.currentDefinition) !== this.savedHash);
    this.canUndo.next(this.past.length > 0);
    this.canRedo.next(this.future.length > 0);
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private hash(def: FormDefinition): string {
    return JSON.stringify(def);
  }

  private createEmptyDefinition(): FormDefinition {
    return {
      id: uuidv4(),
      key: 'new-form',
      title: 'Neues Formular',
      version: 1,
      status: 'draft',
      pages: [{id: uuidv4(), title: 'Seite 1', blocks: []}],
      validations: []
    };
  }

  private findBlockContainer(blocks: FormBlock[], parentBlockId: string): FormBlock[] | null {
    for (const block of blocks) {
      if (block.id === parentBlockId) {
        if (block.type === 'group' || block.type === 'list') {
          return block.blocks;
        }
        return null;
      }
      if (block.type === 'group' || block.type === 'list') {
        const found = this.findBlockContainer(block.blocks, parentBlockId);
        if (found) return found;
      }
    }
    return null;
  }

  private findBlockAndContainer(blocks: FormBlock[], blockId: string): {
    container: FormBlock[],
    index: number
  } | null {
    for (let i = 0; i < blocks.length; i++) {
      if (blocks[i].id === blockId) {
        return {container: blocks, index: i};
      }
      const block = blocks[i];
      if (block.type === 'group' || block.type === 'list') {
        const found = this.findBlockAndContainer(block.blocks, blockId);
        if (found) return found;
      }
    }
    return null;
  }

  // --- Key generation --------------------------------------------------------
  /** Collect every key used anywhere in the definition (for collision checks). */
  private collectKeys(def: FormDefinition): Set<string> {
    const keys = new Set<string>();
    const walk = (blocks: FormBlock[]) => {
      for (const block of blocks) {
        if ('key' in block && block.key) keys.add(block.key);
        if (block.type === 'group' || block.type === 'list') walk(block.blocks);
      }
    };
    def.pages.forEach(p => walk(p.blocks));
    return keys;
  }

  private uniqueKey(base: string, existing: Set<string>): string {
    let candidate = base;
    let i = 2;
    while (existing.has(candidate)) {
      candidate = `${base}_${i++}`;
    }
    return candidate;
  }

  private createDefaultBlock(type: string, def: FormDefinition): FormBlock {
    const id = uuidv4();
    const existing = this.collectKeys(def);
    const label = this.defaultLabel(type);
    const common = {id, label};

    switch (type) {
      case 'group':
        return {...common, type: 'group', key: this.uniqueKey('group', existing), blocks: []} as any;
      case 'list':
        return {...common, type: 'list', key: this.uniqueKey('liste', existing), blocks: [], minItems: 0} as any;
      case 'static-text':
        return {...common, type: 'static-text', key: this.uniqueKey('text', existing), content: '<p>Statischer Text</p>'} as any;
      default: {
        const fieldType = type as FieldType;
        const block: FieldBlock = {
          ...common,
          type: fieldType,
          key: this.uniqueKey(this.keyBase(fieldType), existing),
          validators: {}
        };
        if (OPTION_FIELD_TYPES.includes(fieldType)) {
          block.options = [
            {label: 'Option 1', value: 'option_1'},
            {label: 'Option 2', value: 'option_2'}
          ];
        }
        if (fieldType === 'multiselect') block.multiple = true;
        return block;
      }
    }
  }

  /** Whether a value-bearing field of this type stores an array. */
  static isArrayValueType(type: FieldType): boolean {
    return ARRAY_VALUE_FIELD_TYPES.includes(type);
  }

  private keyBase(type: FieldType): string {
    return ({
      text: 'text',
      textarea: 'text',
      email: 'email',
      phone: 'telefon',
      number: 'zahl',
      select: 'auswahl',
      multiselect: 'auswahl',
      radio: 'auswahl',
      'checkbox-group': 'auswahl',
      date: 'datum',
      time: 'zeit',
      boolean: 'option',
      file: 'datei',
      signature: 'unterschrift'
    } as Record<FieldType, string>)[type] || 'feld';
  }

  private defaultLabel(type: string): string {
    const map: Record<string, string> = {
      text: 'Textfeld',
      textarea: 'Mehrzeiliger Text',
      email: 'E-Mail',
      phone: 'Telefon',
      number: 'Zahl',
      select: 'Auswahl',
      multiselect: 'Mehrfachauswahl',
      radio: 'Optionsfeld',
      'checkbox-group': 'Kontrollkästchen',
      date: 'Datum',
      time: 'Uhrzeit',
      boolean: 'Ja/Nein',
      file: 'Datei-Upload',
      signature: 'Unterschrift',
      group: 'Gruppe',
      list: 'Wiederholbare Liste',
      'static-text': 'Statischer Text'
    };
    return map[type] || `Neues Feld`;
  }

  /** Auto-generate a slug from a label, ensuring uniqueness within the form. */
  slugForLabel(label: string, currentKey?: string): string {
    const base = (label || 'feld')
      .toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'feld';
    const existing = this.collectKeys(this.currentDefinition);
    if (currentKey) existing.delete(currentKey);
    return this.uniqueKey(base, existing);
  }

  /**
   * Can the runtime NOT address this key? True only for a key the form cannot resolve — see
   * {@link isUnaddressableFieldKey}. This is the one that may be shown as an error on an existing definition.
   */
  isKeyUnaddressable(key: string | null | undefined): boolean {
    return isUnaddressableFieldKey(key);
  }

  /** Does this key match the house charset for a newly authored key? See {@link isSlugFieldKey}. */
  isKeySlug(key: string | null | undefined): boolean {
    return isSlugFieldKey(key);
  }

  /** Is this key already used by a different block? */
  isKeyTaken(key: string, ownerBlockId: string): boolean {
    let taken = false;
    const walk = (blocks: FormBlock[]) => {
      for (const block of blocks) {
        if ('key' in block && block.key === key && block.id !== ownerBlockId) taken = true;
        if (block.type === 'group' || block.type === 'list') walk(block.blocks);
      }
    };
    this.currentDefinition.pages.forEach(p => walk(p.blocks));
    return taken;
  }
}
